import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { PaperPlaneTilt, X, Microphone, Waveform, CheckCircle, Clock, SpeakerHigh, SpeakerX, ThumbsUp, ThumbsDown, Sparkle } from "@phosphor-icons/react";
import KoshMark from "./KoshMark";

const SUGGESTIONS = [
  "What do you build?",
  "Show me a case study",
  "Scope my project",
];

const HANDOFF_RX = /\b(let'?s\s+talk|book\s+(a\s+)?call|schedule\s+(a\s+)?(call|meet|discovery)|talk\s+to\s+(someone|the\s+team|sales|founder)|contact\s+(sales|us)|discovery\s+call|meet\s+with|get\s+on\s+a\s+call|book\s+a\s+meeting)\b/i;

const LANGS = [
  { code: "en", label: "EN", locale: "en-US", full: "English" },
  { code: "hi", label: "हि", locale: "hi-IN", full: "हिन्दी" },
  { code: "mr", label: "मरा", locale: "mr-IN", full: "मराठी" },
  { code: "gu", label: "ગુજ", locale: "gu-IN", full: "ગુજરાતી" },
  { code: "kn", label: "ಕನ್", locale: "kn-IN", full: "ಕನ್ನಡ" },
];

const langMeta = (code) => LANGS.find((L) => L.code === code) || LANGS[0];

const SpeechRecognition =
  typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
const VOICE_SUPPORTED = !!SpeechRecognition;
// Voice output now uses server-side OpenAI TTS (works for every language), so it's
// always available regardless of the browser's SpeechSynthesis support.
const TTS_SUPPORTED = true;

const DEFAULT_GREETING = {
  role: "assistant",
  content: "I'm KOSH — Wisewit's AI copilot. Ask what we orchestrate, or describe a problem and I'll route it to the right people.",
};

export default function AICopilotFab() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("ww_chat_session") || null);
  const [lang, setLang] = useState(() => localStorage.getItem("ww_kosh_lang") || "en");
  const [messages, setMessages] = useState([DEFAULT_GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [resumed, setResumed] = useState(false);
  const [showRouter, setShowRouter] = useState(false);
  const [routerSubmitted, setRouterSubmitted] = useState(false);
  const [speakEnabled, setSpeakEnabled] = useState(() => localStorage.getItem("ww_kosh_speak") === "1");
  const [speaking, setSpeaking] = useState(false);
  // message_ids whose reply was just shaped by a visitor correction — badge
  // flashes for a few seconds, then settles into a static state.
  const [justImprovedIds, setJustImprovedIds] = useState(() => new Set());
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const lastSpokenRef = useRef(null);
  const audioRef = useRef(null);

  // Draggable FAB — long-press (450ms) then drag to reposition; persists per-browser.
  const [fabPos, setFabPos] = useState(() => {
    try {
      const raw = localStorage.getItem("ww_kosh_fab_pos");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [dragging, setDragging] = useState(false);
  const pressTimerRef = useRef(null);
  const grabOffsetRef = useRef({ x: 0, y: 0 });
  const justDraggedRef = useRef(false);
  const fabRef = useRef(null);

  const FAB_SIZE = 64;
  const clampPos = (x, y) => ({
    x: Math.max(8, Math.min(x, window.innerWidth - FAB_SIZE - 8)),
    y: Math.max(8, Math.min(y, window.innerHeight - FAB_SIZE - 8)),
  });

  // Snap the FAB to whichever screen corner its centre is nearest to.
  const snapToCorner = (p) => {
    const M = 24;
    const cx = p.x + FAB_SIZE / 2;
    const cy = p.y + FAB_SIZE / 2;
    return {
      x: cx < window.innerWidth / 2 ? M : window.innerWidth - FAB_SIZE - M,
      y: cy < window.innerHeight / 2 ? M : window.innerHeight - FAB_SIZE - M,
    };
  };

  const onFabPointerDown = (e) => {
    // Only left button / touch
    if (e.button && e.button !== 0) return;
    const el = fabRef.current;
    if (el) el.setPointerCapture?.(e.pointerId);
    justDraggedRef.current = false;
    pressTimerRef.current = setTimeout(() => {
      const rect = el?.getBoundingClientRect();
      if (rect) grabOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      setDragging(true);
      justDraggedRef.current = true; // long-press engaged → suppress the tap/open
      if (navigator.vibrate) try { navigator.vibrate(15); } catch { /* ignore */ }
    }, 450);
  };

  const onFabPointerMove = (e) => {
    if (!dragging) return;
    e.preventDefault();
    const next = clampPos(e.clientX - grabOffsetRef.current.x, e.clientY - grabOffsetRef.current.y);
    setFabPos(next);
  };

  const endFabPress = () => {
    if (pressTimerRef.current) { clearTimeout(pressTimerRef.current); pressTimerRef.current = null; }
    if (dragging) {
      setDragging(false);
      setFabPos((p) => {
        const snapped = p ? snapToCorner(p) : p;
        if (snapped) { try { localStorage.setItem("ww_kosh_fab_pos", JSON.stringify(snapped)); } catch { /* ignore */ } }
        return snapped;
      });
    }
  };

  // Keep the FAB on-screen if the window is resized smaller.
  useEffect(() => {
    if (!fabPos) return;
    const onResize = () => setFabPos((p) => (p ? clampPos(p.x, p.y) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [fabPos]);

  const changeLang = (next) => {
    setLang(next);
    localStorage.setItem("ww_kosh_lang", next);
    stopAudio();
  };

  const toggleSpeak = () => {
    const next = !speakEnabled;
    setSpeakEnabled(next);
    localStorage.setItem("ww_kosh_speak", next ? "1" : "0");
    if (!next) stopAudio();
  };

  const stopAudio = () => {
    const a = audioRef.current;
    if (a) {
      try { a.pause(); a.currentTime = 0; } catch { /* ignore */ }
      if (a.src) { try { URL.revokeObjectURL(a.src); } catch { /* ignore */ } a.removeAttribute("src"); }
    }
    setSpeaking(false);
  };

  // Server-side TTS (OpenAI): speaks every language properly — including
  // Marathi/Gujarati/Kannada, which browser voices can't handle. Cached backend-side.
  const speak = async (text) => {
    try {
      stopAudio();
      setSpeaking(true);
      const { data } = await api.post("/ai/tts", { text, lang }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      let a = audioRef.current;
      if (!a) { a = new Audio(); audioRef.current = a; }
      a.src = url;
      a.onended = () => setSpeaking(false);
      a.onerror = () => setSpeaking(false);
      await a.play();
    } catch { setSpeaking(false); }
  };

  // Voice-reply: speak the latest assistant message when opt-in is on.
  useEffect(() => {
    if (!speakEnabled || !open) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return;
    // Skip the default greeting; it fires on every mount and would auto-speak on every reload.
    if (last.content === DEFAULT_GREETING.content) return;
    if (lastSpokenRef.current === last.content) return;
    lastSpokenRef.current = last.content;
    speak(last.content);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, speakEnabled, open, lang]);

  // Silence KOSH when the panel closes
  useEffect(() => {
    if (!open) stopAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, showRouter]);

  // Memory: on mount, if we have a stored session, resume history.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/ai/chat/${sessionId}`);
        const past = (data?.messages || []).filter((m) => m.role === "user" || m.role === "assistant");
        if (!cancelled && past.length > 0) {
          setMessages([
            DEFAULT_GREETING,
            ...past.map((m) => {
              const clusterSizes = m.applied_cluster_sizes || {};
              const visitorCount = Object.values(clusterSizes).reduce((s, n) => s + (Number(n) || 0), 0);
              return {
                role: m.role,
                content: m.content,
                message_id: m.id,
                rating: m.rating,
                rating_note: m.rating_note,
                corrections_applied: (m.applied_corrections || []).length,
                correction_visitor_count: visitorCount || (m.applied_corrections || []).length,
              };
            }),
          ]);
          setResumed(true);
        }
      } catch { /* silent — treat as fresh session */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global open handler: lets Hero KOSH card + solutions atlas + any CTA open the copilot.
  useEffect(() => {
    const handler = (e) => {
      setOpen(true);
      const seed = e?.detail?.seed;
      if (seed) setTimeout(() => sendMessage(seed), 200);
    };
    window.addEventListener("kosh:open", handler);
    return () => window.removeEventListener("kosh:open", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, busy]);

  // Auto-dismiss the "resumed" badge after 6s so it doesn't linger forever.
  useEffect(() => {
    if (!resumed || !open) return;
    const t = setTimeout(() => setResumed(false), 6000);
    return () => clearTimeout(t);
  }, [resumed, open]);

  const maybeTriggerHandoff = (text) => {
    if (routerSubmitted || showRouter) return;
    if (HANDOFF_RX.test(text || "")) setShowRouter(true);
  };

  const sendMessage = async (text) => {
    if (!text?.trim() || busy) return;
    const userMsg = { role: "user", content: text.trim() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setBusy(true);
    maybeTriggerHandoff(userMsg.content);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId, message: userMsg.content, lang });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem("ww_chat_session", data.session_id);
      }
      setMessages((m) => [...m, {
        role: "assistant",
        content: data.reply,
        message_id: data.message_id,
        corrections_applied: data.corrections_applied || 0,
        correction_visitor_count: data.correction_visitor_count || data.corrections_applied || 0,
      }]);
      if (data.corrections_applied > 0 && data.message_id) {
        setJustImprovedIds((s) => {
          const next = new Set(s);
          next.add(data.message_id);
          return next;
        });
        // Fade the flash after 4.5s — badge stays, animation stops.
        setTimeout(() => {
          setJustImprovedIds((s) => {
            const next = new Set(s);
            next.delete(data.message_id);
            return next;
          });
        }, 4500);
      }
      maybeTriggerHandoff(data.reply);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Signal lost. Try again." }]);
    } finally {
      setBusy(false);
    }
  };

  const send = (e) => { e?.preventDefault(); sendMessage(input); };

  const rateMessage = async (messageId, rating, note) => {
    if (!messageId) return;
    // Optimistic UI
    setMessages((m) => m.map((msg) => (msg.message_id === messageId ? { ...msg, rating, rating_note: note } : msg)));
    try {
      await api.post(`/ai/chat/messages/${messageId}/rate`, note ? { rating, note } : { rating });
    } catch {
      setMessages((m) => m.map((msg) => (msg.message_id === messageId ? { ...msg, rating: undefined, rating_note: undefined } : msg)));
    }
  };

  const [notePrompt, setNotePrompt] = useState(null); // { message_id } | null
  const [noteDraft, setNoteDraft] = useState("");

  const openNotePrompt = (messageId) => {
    setNotePrompt({ message_id: messageId });
    setNoteDraft("");
  };
  const submitNote = async (e) => {
    e?.preventDefault();
    if (!notePrompt?.message_id) return;
    await rateMessage(notePrompt.message_id, "down", noteDraft.trim() || undefined);
    setNotePrompt(null);
    setNoteDraft("");
  };

  const toggleVoice = () => {
    if (!VOICE_SUPPORTED) return;
    if (listening) { recognitionRef.current?.stop(); return; }
    const rec = new SpeechRecognition();
    rec.lang = langMeta(lang).locale;
    rec.interimResults = true;
    rec.continuous = false;
    rec.onstart = () => setListening(true);
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    rec.onresult = (evt) => {
      const transcript = Array.from(evt.results).map((r) => r[0]?.transcript || "").join("");
      setInput(transcript);
    };
    recognitionRef.current = rec;
    try { rec.start(); } catch { setListening(false); }
  };

  const showSuggestions = messages.length === 1 && !busy && !resumed;

  return (
    <>
      <button
        data-testid="copilot-fab"
        ref={fabRef}
        aria-label="Open KOSH copilot"
        onPointerDown={onFabPointerDown}
        onPointerMove={onFabPointerMove}
        onPointerUp={endFabPress}
        onPointerCancel={endFabPress}
        onClick={() => {
          if (justDraggedRef.current) { justDraggedRef.current = false; return; }
          setOpen((v) => !v);
        }}
        className={`fixed z-50 w-16 h-16 flex items-center justify-center group ${
          fabPos ? "" : "bottom-24 right-6"
        } ${dragging ? "scale-110 cursor-grabbing" : "transition-all hover:scale-105 cursor-grab"}`}
        style={{
          backgroundColor: "#FF4500",
          boxShadow: dragging
            ? "0 12px 40px rgba(255, 69, 0, 0.6), inset 0 0 0 2px rgba(255,255,255,0.28)"
            : "0 8px 28px rgba(255, 69, 0, 0.42), inset 0 0 0 1.5px rgba(255,255,255,0.14)",
          borderRadius: "9999px",
          touchAction: "none",
          ...(fabPos ? { left: `${fabPos.x}px`, top: `${fabPos.y}px`, right: "auto", bottom: "auto" } : {}),
        }}
        title="Long-press to move me"
      >
        {open ? (
          <X size={22} color="#FFFFFF" weight="bold" />
        ) : (
          <>
            <KoshMark size={28} tone="on-color" />
            {!dragging && (
              <span
                className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-2.5 py-1 bg-[#050505] border border-[#FF4500] font-mono text-[10px] tracking-[0.28em] uppercase text-[#FF4500] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ pointerEvents: "none" }}
                data-testid="copilot-nameplate"
              >
                ◢ KOSH
              </span>
            )}
          </>
        )}
      </button>

      {open && (
        <div
          data-testid="copilot-panel"
          className="fixed bottom-44 right-6 z-50 w-[92vw] max-w-md h-[560px] flex flex-col ww-glass"
        >
          <div className="flex items-center justify-between px-4 h-14 border-b border-[#2A2A2A]">
            <div className="flex items-center gap-3">
              <KoshMark size={26} tone="on-glass" />
              <div>
                <div className="font-display text-base leading-none" style={{ color: "var(--ww-heading)" }}>KOSH</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FF4500] ww-pulse" />
                  <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#8E8E93]">Wisewit AI copilot</span>
                </div>
              </div>
            </div>
            <span className="font-mono text-[9px] text-[#555]">{sessionId ? sessionId.slice(0, 8) : "new"}</span>
          </div>

          {/* Language toggle */}
          <div className="flex items-center gap-1 px-3 pt-2 flex-wrap" data-testid="copilot-lang-toggle">
            {LANGS.map((L) => (
              <button
                key={L.code}
                onClick={() => changeLang(L.code)}
                data-testid={`copilot-lang-${L.code}`}
                title={L.full}
                className={`text-[10px] font-mono tracking-[0.14em] uppercase px-2 py-1 border transition-colors ${
                  lang === L.code
                    ? "border-[#DFFF00] text-[#DFFF00]"
                    : "border-[#2A2A2A] text-[#8E8E93] hover:text-white"
                }`}
                aria-label={`Switch KOSH to ${L.full}`}
              >
                {L.label}
              </button>
            ))}
            <span className="ml-1 font-mono text-[9px] text-[#555]">
              → {langMeta(lang).full}
            </span>
            {TTS_SUPPORTED && (
              <button
                onClick={toggleSpeak}
                data-testid="copilot-speak-toggle"
                title={speakEnabled ? "Mute KOSH voice replies" : "Have KOSH speak its replies"}
                className={`ml-auto text-[10px] font-mono tracking-[0.14em] uppercase px-2 py-1 border transition-colors flex items-center gap-1.5 ${
                  speakEnabled
                    ? (speaking ? "border-[#00FFB2] text-[#00FFB2] ww-pulse" : "border-[#00FFB2] text-[#00FFB2]")
                    : "border-[#2A2A2A] text-[#8E8E93] hover:text-white"
                }`}
              >
                {speakEnabled ? <SpeakerHigh size={11} weight="fill" /> : <SpeakerX size={11} />}
                Voice
              </button>
            )}
          </div>

          {/* Memory badge — fades out after 6s */}
          {resumed && (
            <div
              className="flex items-center gap-2 px-4 py-2 border-b border-[#DFFF00]/20 bg-[#DFFF00]/[0.05]"
              data-testid="copilot-resumed-badge"
            >
              <Clock size={12} color="#DFFF00" weight="fill" />
              <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#DFFF00]">
                Picking up where we left off
              </span>
              <button
                onClick={() => setResumed(false)}
                className="ml-auto text-[#8E8E93] hover:text-white"
                data-testid="copilot-resumed-dismiss"
                aria-label="Dismiss"
              >
                <X size={12} />
              </button>
            </div>
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="copilot-messages">
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
                <div
                  className={`max-w-[88%] px-3 py-2 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#FF4500] text-white"
                      : "bg-[#121212] text-[#F5F5F5] border border-[#2A2A2A]"
                  }`}
                  style={m.role === "user" ? {} : { color: "var(--ww-text)" }}
                >
                  {m.content}
                  {/* Improved-via-feedback badge — flashes when a visitor correction actively shaped this reply */}
                  {m.role === "assistant" && m.message_id && m.corrections_applied > 0 && (
                    <div
                      data-testid={`copilot-improved-${m.message_id}`}
                      className={`mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 border border-[#DFFF00]/40 bg-[#DFFF00]/10 text-[#DFFF00] font-mono text-[9px] tracking-[0.16em] uppercase ${justImprovedIds.has(m.message_id) ? "animate-pulse" : "opacity-70"}`}
                      title={m.correction_visitor_count > 1 ? `Shaped by feedback from ${m.correction_visitor_count} visitor${m.correction_visitor_count === 1 ? "" : "s"}` : "This reply was shaped by a prior visitor correction"}
                    >
                      <Sparkle size={11} weight="fill" />
                      <span>Improved via feedback{m.correction_visitor_count > 1 ? ` · ${m.correction_visitor_count} visitors` : ""}</span>
                    </div>
                  )}
                  {/* Thumbs — only on assistant messages that have a message_id (i.e. NOT the default greeting) */}
                  {m.role === "assistant" && m.message_id && (
                    <div className="mt-2 pt-2 border-t border-[#2A2A2A]/60 flex items-center gap-2" data-testid={`copilot-rate-${m.message_id}`}>
                      <span className="font-mono text-[9px] tracking-[0.16em] uppercase text-[#555]">
                        {m.rating === "up" ? "Rated helpful" : m.rating === "down" ? "Rated poor" : "Was this helpful?"}
                      </span>
                      <button
                        onClick={() => rateMessage(m.message_id, "up")}
                        disabled={!!m.rating}
                        title="Helpful"
                        data-testid={`copilot-thumb-up-${m.message_id}`}
                        className={`ml-auto transition-colors ${m.rating === "up" ? "text-[#00FFB2]" : (m.rating ? "text-[#333]" : "text-[#8E8E93] hover:text-[#00FFB2]")}`}
                      >
                        <ThumbsUp size={13} weight={m.rating === "up" ? "fill" : "regular"} />
                      </button>
                      <button
                        onClick={() => openNotePrompt(m.message_id)}
                        disabled={!!m.rating}
                        title="Not helpful"
                        data-testid={`copilot-thumb-down-${m.message_id}`}
                        className={`transition-colors ${m.rating === "down" ? "text-[#FF3060]" : (m.rating ? "text-[#333]" : "text-[#8E8E93] hover:text-[#FF3060]")}`}
                      >
                        <ThumbsDown size={13} weight={m.rating === "down" ? "fill" : "regular"} />
                      </button>
                    </div>
                  )}
                  {/* Inline note prompt when the visitor clicks 👎 */}
                  {m.role === "assistant" && m.message_id && notePrompt?.message_id === m.message_id && (
                    <form
                      onSubmit={submitNote}
                      data-testid={`copilot-note-prompt-${m.message_id}`}
                      className="mt-2 pt-2 border-t border-[#FF3060]/40 space-y-2"
                    >
                      <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-[#FF3060]">
                        What should KOSH have said? <span className="text-[#555]">(optional)</span>
                      </div>
                      <textarea
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        rows={2}
                        placeholder="One line is enough — feeds training data"
                        data-testid={`copilot-note-input-${m.message_id}`}
                        className="w-full bg-transparent border border-[#2A2A2A] focus:border-[#FF3060] px-2 py-1 text-xs text-white outline-none resize-none"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => submitNote()}
                          data-testid={`copilot-note-skip-${m.message_id}`}
                          className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#8E8E93] hover:text-white px-2 py-1"
                        >
                          Skip
                        </button>
                        <button
                          type="submit"
                          data-testid={`copilot-note-submit-${m.message_id}`}
                          className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#FF3060] hover:text-white border border-[#FF3060] px-2 py-1"
                        >
                          Send feedback
                        </button>
                      </div>
                    </form>
                  )}
                  {m.role === "assistant" && m.rating === "down" && m.rating_note && (
                    <div className="mt-1 pl-2 border-l-2 border-[#FF3060] text-[10px] text-[#8E8E93] italic" data-testid={`copilot-note-saved-${m.message_id}`}>
                      "{m.rating_note}"
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="bg-[#121212] border border-[#2A2A2A] px-3 py-2 max-w-[60%] flex items-center gap-2">
                <KoshMark size={14} tone="on-glass" />
                <span className="font-mono text-xs text-[#FF4500]">KOSH is thinking…</span>
              </div>
            )}

            {showSuggestions && (
              <div className="pt-2" data-testid="copilot-suggestions">
                <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#8E8E93] mb-2">◢ Try one</div>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => sendMessage(s)}
                      data-testid={`copilot-suggestion-${s.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 20)}`}
                      className="text-[11px] font-mono tracking-[0.08em] px-3 py-1.5 border border-[#FF4500]/40 text-[#F5F5F5] hover:border-[#FF4500] hover:bg-[#FF4500]/10 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Inline Project Router mini-card — triggered by handoff intent */}
            {showRouter && !routerSubmitted && (
              <InlineRouterCard
                onSubmitted={() => setRouterSubmitted(true)}
                onDismiss={() => setShowRouter(false)}
              />
            )}
            {routerSubmitted && (
              <div
                className="mt-2 border border-[#00FFB2] bg-[#00FFB2]/[0.06] p-3 flex items-center gap-3"
                data-testid="copilot-router-confirm"
              >
                <CheckCircle size={18} weight="fill" color="#00FFB2" />
                <div className="text-sm text-[#F5F5F5]">
                  Routed. The Wisewit team will reach out shortly.
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="flex items-center gap-2 px-3 h-14 border-t border-[#2A2A2A]">
            <input
              data-testid="copilot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={listening ? "Listening…" : "Ask KOSH anything…"}
              className="flex-1 bg-transparent text-white text-sm outline-none font-body"
            />
            {VOICE_SUPPORTED && (
              <button
                type="button"
                onClick={toggleVoice}
                data-testid="copilot-voice"
                aria-label={listening ? "Stop listening" : "Talk to KOSH"}
                title={listening ? "Tap to stop" : "Tap to talk to KOSH"}
                className={`transition-colors ${listening ? "text-[#00FFB2] ww-pulse" : "text-[#8E8E93] hover:text-[#DFFF00]"}`}
              >
                {listening ? <Waveform size={20} weight="fill" /> : <Microphone size={20} weight="fill" />}
              </button>
            )}
            <button
              type="submit"
              data-testid="copilot-send"
              disabled={busy || !input.trim()}
              className="text-[#FF4500] disabled:text-[#555] transition-colors"
            >
              <PaperPlaneTilt size={20} weight="fill" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

/**
 * Inline Project Router — a 3-field mini form that slides into the chat
 * when KOSH detects handoff intent. Posts to /api/project-router just like
 * the full /contact page so the recruiter gets the same email + inbox row.
 */
function InlineRouterCard({ onSubmitted, onDismiss }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [challenge, setChallenge] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (!name.trim() || !email.trim() || !challenge.trim()) {
      setErr("All three fields are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/project-router", {
        name: name.trim(),
        email: email.trim(),
        project_type: "not-sure",
        challenge: challenge.trim(),
      });
      onSubmitted?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="copilot-router-card"
      className="mt-3 border border-[#FF4500] bg-[#FF4500]/[0.05] p-3 space-y-2"
    >
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#FF4500]">
          ◢ Book a call with Wisewit
        </div>
        <button type="button" onClick={onDismiss} className="text-[#8E8E93] hover:text-white" data-testid="copilot-router-close">
          <X size={12} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          data-testid="copilot-router-name"
          className="ww-input text-xs py-1.5"
        />
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          type="email"
          placeholder="you@company.com"
          data-testid="copilot-router-email"
          className="ww-input text-xs py-1.5"
        />
      </div>
      <textarea
        value={challenge}
        onChange={(e) => setChallenge(e.target.value)}
        rows={2}
        placeholder="One line on what you're trying to solve"
        data-testid="copilot-router-challenge"
        className="ww-input text-xs py-1.5 resize-none"
      />
      {err && <div className="font-mono text-[10px] text-[#FF3060]">{err}</div>}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="submit"
          disabled={busy}
          data-testid="copilot-router-submit"
          className="btn-primary text-[11px] py-1.5 px-3 disabled:opacity-50"
        >
          {busy ? "Routing…" : "Route to team →"}
        </button>
      </div>
    </form>
  );
}
